import { Component, OnInit } from '@angular/core';
import { AuctionStatus, SaleStatus } from 'src/app/API.service';
import { AuctionReportLineItem, AuctionReportModel } from '../../model/auction.model';
import { AuctionModelService } from '../../model/auction.model';

interface ISortingConfig {
  [key: string]: {
    field: string,
  };
}

interface AuctionReportMessageEvent {
  message: 'updatePaymentStatus' | 'splitCompleted' | 'adjustmentCompleted' | 'saleStatusChanged';
  lot: AuctionReportLineItem;
}

const DESC = 'DESC';
const ASC = 'ASC';

@Component({
  selector: 'app-auction-report-lot-list',
  styleUrls: ['./auction-report-lot-list.component.scss'],
  templateUrl: './auction-report-lot-list.component.html',
})
export class AuctionReportLotListComponent implements OnInit {

  constructor(private auctionModel: AuctionModelService) {

  }

  public processing: boolean = false;
  public filteredList: AuctionReportLineItem[] = [];
  public sortingConfig: ISortingConfig;
  private sortField = 'finalBid';
  private sortDirection = DESC;
  private lotLimit = 50;
  private lastItemId: string | null = null;
  public auctionReport: AuctionReportModel = new AuctionReportModel();

  public ngOnInit(): void {
    this.sortingConfig = {
      finalBid: {
        field: 'FINAL_BID',
      },
      winningBidder: {
        field: 'WINNING_BUYER',
      },
    };
  }

  public get auctionStatus() {
    return this.auctionModel && this.auctionModel.activeAuction && this.auctionModel.activeAuction.status;
  }

  public onSort(element: string): void {
    this.sortDirection = this.sortDirection === DESC ? ASC : DESC;
    this.sortField = element;
    this.refreshList();
  }

  public isSortedAsc(fieldName: string): boolean {
    return this.sortField === fieldName && this.sortDirection === ASC;
  }

  public isSortedDesc(fieldName: string): boolean {
    return this.sortField === fieldName && this.sortDirection === DESC;
  }

  public async refreshList(): Promise<void> {
    this.auctionReport = await this.auctionModel.loadAuctionReport(
      this.auctionModel.activeAuction.auctionId,
      this.sortingConfig[this.sortField].field,
      this.sortDirection,
      this.lotLimit,
      this.lastItemId
    )
    // console.log("auctionReport",this.auctionReport);
  }

  public filterByStatus(lineItems: AuctionReportLineItem[], saleStatus: SaleStatus): AuctionReportLineItem[] {
    return lineItems.filter(x => x.saleStatus === saleStatus);
  }

  public async receiveMessage($event: AuctionReportMessageEvent): Promise<void> {
    if ($event.message === 'updatePaymentStatus') {
      const lot = $event.lot;
      const winningBidderEmail = lot.winningBidderEmail;
      // Additional processing can be added here if needed
    } else if ($event.message === 'splitCompleted') {
      // Refresh the auction report to show the new split line items
      await this.refreshList();
    } else if ($event.message === 'adjustmentCompleted' || $event.message === 'saleStatusChanged') {
      // Adjustments and status changes update the model directly
      // Angular change detection will automatically recalculate totals
      // No additional action needed - the calculation methods will be re-evaluated
    }
  }

  /**
   * Calculates the gross sale total for the entire auction.
   * This is the sum of every Sold lot's "Final Price" without any deductions.
   * Excludes No Sale (UNSOLD) lots.
   *
   * Note: The calculation preserves decimal precision (e.g., 1000.50 + 2000.75 = 3001.25).
   * The return value is a number that may include decimal places.
   */
  public getGrossSaleTotal(): number {
    if (!this.auctionReport || !this.auctionReport.lineItems) {
      return 0;
    }

    const soldLots = this.filterByStatus(this.auctionReport.lineItems, SaleStatus.SOLD);
    return soldLots.reduce((total, lot) => {
      const finalPrice = lot.finalPrice;
      // Handle null, undefined, or NaN values
      if (finalPrice == null || isNaN(finalPrice)) {
        return total;
      }
      // Addition preserves decimal precision - no integer conversion
      return total + finalPrice;
    }, 0);
  }

  /**
   * Formats the gross sale total as currency with dollar sign, comma separators, and two decimal places.
   */
  public getFormattedGrossSaleTotal(): string {
    const total = this.getGrossSaleTotal();
    return `$${total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

}
